import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateShort, formatTime } from "@/lib/date";
import { ATID } from "@/lib/adminTestIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, Star, AlertCircle, MoreVertical, Edit3, Eye, Copy, Send, FileText,
  Archive, Trash2, RotateCcw, ChevronLeft, ChevronRight, PlusSquare,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PAGE_SIZE = 15;

const STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "draft", label: "Borrador" },
  { value: "pending", label: "Pendiente" },
  { value: "scheduled", label: "Programada" },
  { value: "published", label: "Publicada" },
  { value: "archived", label: "Archivada" },
  { value: "trash", label: "Papelera" },
];

const CONTENT_TYPE_OPTIONS = [
  { value: "", label: "Todos los tipos" },
  { value: "article", label: "Noticia" },
  { value: "breaking", label: "Último momento" },
  { value: "live", label: "En vivo" },
  { value: "opinion", label: "Opinión" },
  { value: "interview", label: "Entrevista" },
  { value: "video", label: "Video" },
  { value: "gallery", label: "Galería" },
  { value: "press_release", label: "Comunicado" },
  { value: "sponsored", label: "Contenido patrocinado" },
];

const ORIGIN_OPTIONS = [
  { value: "", label: "Cualquier origen" },
  { value: "manual", label: "Manual" },
  { value: "automated", label: "Automatizado" },
];

const STATUS_META = {
  draft: { label: "Borrador", cls: "bg-lightgray text-textgray" },
  pending: { label: "Pendiente", cls: "bg-yellow-100 text-yellow-800" },
  scheduled: { label: "Programada", cls: "bg-blue-100 text-blue-800" },
  published: { label: "Publicada", cls: "bg-watergreen/20 text-nightblue" },
  archived: { label: "Archivada", cls: "bg-softblue/40 text-nightblue" },
  trash: { label: "Papelera", cls: "bg-red-100 text-red-700" },
};

export default function AdminArticlesListPage() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, loading: true });
  const [confirm, setConfirm] = useState(null); // { action, article }
  const { user: me, hasPerm, isAdmin } = useAuth();

  const canPublish = isAdmin || hasPerm("articles.publish");
  const canApprove = isAdmin || hasPerm("articles.approve");
  const canDelete = isAdmin || hasPerm("articles.delete");
  const canEditAny = isAdmin || hasPerm("articles.edit_any");
  const canDraftOnly = !isAdmin && hasPerm("articles.draft_only");
  const canSubmit = isAdmin || hasPerm("articles.submit_for_review");
  const canEditArticle = (a) => {
    if (isAdmin || canEditAny) return true;
    return a.created_by === me?.id || a.author_id === me?.id;
  };

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    category: params.get("category") || "",
    status: params.get("status") || "",
    content_type: params.get("content_type") || "",
    origin_type: params.get("origin_type") || "",
    date_from: params.get("date_from") || "",
    date_to: params.get("date_to") || "",
    page: Math.max(1, parseInt(params.get("page") || "1", 10)),
  }), [params]);

  useEffect(() => {
    api.adminListCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const reload = React.useCallback(() => {
    setData((d) => ({ ...d, loading: true }));
    const query = {
      q: filters.q || undefined,
      category: filters.category || undefined,
      status: filters.status || undefined,
      content_type: filters.content_type || undefined,
      origin_type: filters.origin_type || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
      include_trash: filters.status === "trash",
      limit: PAGE_SIZE,
      skip: (filters.page - 1) * PAGE_SIZE,
    };
    api.adminListArticles(query)
      .then((r) => setData({ items: r.items, total: r.total, loading: false }))
      .catch(() => setData({ items: [], total: 0, loading: false }));
  }, [filters]);

  useEffect(() => { reload(); }, [reload]);

  const updateFilter = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, v);
    });
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const runAction = async (article, action) => {
    try {
      await api.adminArticleAction(article.id, { action });
      toast.success("Acción aplicada.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar la acción.");
    }
  };

  const duplicate = async (article) => {
    try {
      const clone = await api.adminDuplicateArticle(article.id);
      toast.success("Noticia duplicada. Ahora es un borrador.");
      window.location.assign(`/admin/noticias/${clone.id}`);
    } catch {
      toast.error("No se pudo duplicar.");
    }
  };

  const confirmDelete = async () => {
    if (!confirm) return;
    try {
      await api.adminDeleteArticle(confirm.article.id);
      toast.success("Noticia eliminada definitivamente.");
      setConfirm(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  return (
    <div data-testid={ATID.articlesList}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Contenidos</div>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Noticias</h1>
        </div>
        <Link
          to="/admin/noticias/nueva"
          className="inline-flex items-center gap-2 bg-nightblue hover:bg-blueblack text-white h-10 px-4 text-xs font-semibold uppercase tracking-widest transition-colors"
        >
          <PlusSquare className="w-4 h-4" /> Nueva noticia
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white border border-softblue p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="lg:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
          <Input
            data-testid={ATID.articlesFilterQ}
            placeholder="Buscar por título, autor o etiqueta…"
            value={filters.q}
            onChange={(e) => updateFilter({ q: e.target.value })}
            className="pl-9 h-10 bg-lightgray border-softblue"
          />
        </div>
        <select
          data-testid={ATID.articlesFilterCategory}
          value={filters.category}
          onChange={(e) => updateFilter({ category: e.target.value })}
          className="h-10 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
        >
          <option value="">Todas las categorías</option>
          {categories.map((c) => (
            <option key={c.id} value={c.slug}>{c.name}</option>
          ))}
        </select>
        <select
          data-testid={ATID.articlesFilterStatus}
          value={filters.status}
          onChange={(e) => updateFilter({ status: e.target.value })}
          className="h-10 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          data-testid={ATID.articlesFilterContentType}
          value={filters.content_type}
          onChange={(e) => updateFilter({ content_type: e.target.value })}
          className="h-10 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
        >
          {CONTENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          data-testid={ATID.articlesFilterOrigin}
          value={filters.origin_type}
          onChange={(e) => updateFilter({ origin_type: e.target.value })}
          className="h-10 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
        >
          {ORIGIN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Input
          data-testid={ATID.articlesFilterDateFrom}
          type="date"
          value={filters.date_from ? filters.date_from.substring(0, 10) : ""}
          onChange={(e) => updateFilter({ date_from: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
          className="h-10 bg-lightgray border-softblue"
        />
        <Input
          data-testid={ATID.articlesFilterDateTo}
          type="date"
          value={filters.date_to ? filters.date_to.substring(0, 10) : ""}
          onChange={(e) => updateFilter({ date_to: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
          className="h-10 bg-lightgray border-softblue"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-softblue overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-lightgray text-textgray text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-3 py-3 w-16">Foto</th>
                <th className="text-left px-3 py-3">Título</th>
                <th className="text-left px-3 py-3">Categoría</th>
                <th className="text-left px-3 py-3">Autor</th>
                <th className="text-left px-3 py-3">Estado</th>
                <th className="text-left px-3 py-3">Creada</th>
                <th className="text-left px-3 py-3">Publicada</th>
                <th className="text-center px-3 py-3">Marcas</th>
                <th className="text-right px-3 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.loading && (
                <tr><td colSpan={9} className="px-3 py-8 text-center text-textgray">Cargando…</td></tr>
              )}
              {!data.loading && data.items.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-textgray">No se encontraron noticias con estos filtros.</td></tr>
              )}
              {!data.loading && data.items.map((a) => {
                const s = STATUS_META[a.status] || { label: a.status, cls: "bg-lightgray" };
                return (
                  <tr key={a.id} data-testid={ATID.articlesRow(a.id)} className="border-t border-softblue hover:bg-lightgray/40 transition-colors">
                    <td className="px-3 py-3">
                      {a.main_image ? (
                        <img src={a.main_image} alt="" className="w-12 h-12 object-cover" loading="lazy" />
                      ) : (
                        <div className="w-12 h-12 bg-lightgray border border-softblue" />
                      )}
                    </td>
                    <td className="px-3 py-3 min-w-[220px] max-w-md">
                      <Link
                        to={`/admin/noticias/${a.id}`}
                        data-testid={ATID.articleEditLink(a.id)}
                        className="font-semibold text-blueblack hover:text-petroleum line-clamp-2"
                      >
                        {a.title}
                      </Link>
                      <div className="text-[11px] text-textgray mt-0.5 line-clamp-1">/{a.slug}</div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-textgray">{a.main_category_name}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-textgray">{a.author_name}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-textgray text-xs">
                      {formatDateShort(a.created_at)}<br />
                      <span className="text-textgray/70">{formatTime(a.created_at)} hs</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-textgray text-xs">
                      {a.published_at ? (
                        <>{formatDateShort(a.published_at)}<br />
                          <span className="text-textgray/70">{formatTime(a.published_at)} hs</span></>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <div className="inline-flex items-center gap-1">
                        {a.is_featured && (
                          <span title="Destacada" className="inline-flex items-center justify-center h-6 w-6 bg-watergreen/20 text-watergreen">
                            <Star className="w-3.5 h-3.5 fill-current" />
                          </span>
                        )}
                        {a.is_breaking && (
                          <span title="Último momento" className="inline-flex items-center justify-center h-6 w-6 bg-turquoise/20 text-turquoise">
                            <AlertCircle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {a.status === "published" && (
                          <a
                            href={`/noticia/${a.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={ATID.articlePreviewLink(a.id)}
                            title="Previsualizar"
                            className="inline-flex items-center justify-center h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                        )}
                        <Link
                          to={`/admin/noticias/${a.id}`}
                          title="Editar"
                          className="inline-flex items-center justify-center h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={ATID.articleActionMenu(a.id)}
                              title="Más acciones"
                              className="inline-flex items-center justify-center h-8 w-8 border border-softblue text-nightblue hover:bg-lightgray"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-52">
                            {a.status !== "published" && canPublish && (
                              <DropdownMenuItem onClick={() => runAction(a, "publish")}>
                                <Send className="w-4 h-4 mr-2" /> Publicar
                              </DropdownMenuItem>
                            )}
                            {a.status === "published" && canEditAny && (
                              <DropdownMenuItem onClick={() => runAction(a, "unpublish")}>
                                <FileText className="w-4 h-4 mr-2" /> Despublicar (a borrador)
                              </DropdownMenuItem>
                            )}
                            {a.status !== "draft" && a.status !== "trash" && canEditArticle(a) && (
                              <DropdownMenuItem onClick={() => runAction(a, "draft")}>
                                <FileText className="w-4 h-4 mr-2" /> Enviar a borrador
                              </DropdownMenuItem>
                            )}
                            {a.status !== "pending" && a.status !== "trash" && canSubmit && !canDraftOnly && (
                              <DropdownMenuItem onClick={() => runAction(a, "pending")}>
                                <FileText className="w-4 h-4 mr-2" /> Pendiente de revisión
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => duplicate(a)}>
                              <Copy className="w-4 h-4 mr-2" /> Duplicar
                            </DropdownMenuItem>
                            {canPublish && (
                              <>
                                <DropdownMenuItem onClick={() => runAction(a, "toggle_featured")}>
                                  <Star className="w-4 h-4 mr-2" /> {a.is_featured ? "Quitar destacada" : "Marcar destacada"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => runAction(a, "toggle_breaking")}>
                                  <AlertCircle className="w-4 h-4 mr-2" /> {a.is_breaking ? "Quitar último momento" : "Marcar último momento"}
                                </DropdownMenuItem>
                              </>
                            )}
                            {a.status !== "archived" && a.status !== "trash" && canDelete && (
                              <DropdownMenuItem onClick={() => runAction(a, "archive")}>
                                <Archive className="w-4 h-4 mr-2" /> Archivar
                              </DropdownMenuItem>
                            )}
                            {a.status !== "trash" && canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => runAction(a, "trash")}>
                                  <Trash2 className="w-4 h-4 mr-2 text-red-600" />
                                  <span className="text-red-600">Mover a papelera</span>
                                </DropdownMenuItem>
                              </>
                            )}
                            {a.status === "trash" && canEditAny && (
                              <>
                                <DropdownMenuItem onClick={() => runAction(a, "restore")}>
                                  <RotateCcw className="w-4 h-4 mr-2" /> Restaurar
                                </DropdownMenuItem>
                                {canDelete && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => setConfirm({ action: "delete", article: a })}>
                                      <Trash2 className="w-4 h-4 mr-2 text-red-600" />
                                      <span className="text-red-600">Eliminar definitivamente</span>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-softblue px-4 py-3 text-xs text-textgray">
          <div>
            Mostrando {data.items.length} de {data.total} noticia{data.total === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={filters.page <= 1}
              onClick={() => updateFilter({ page: filters.page - 1 })}
              className="rounded-none border-softblue h-8 px-3"
            >
              <ChevronLeft className="w-4 h-4" /> Anterior
            </Button>
            <span className="uppercase tracking-widest text-[11px] font-semibold">
              Página {filters.page} de {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={filters.page >= totalPages}
              onClick={() => updateFilter({ page: filters.page + 1 })}
              className="rounded-none border-softblue h-8 px-3"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La noticia <span className="font-semibold">{confirm?.article?.title}</span> se eliminará permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
